import type { GraphNode } from './types';
import { typeColors } from './config';
import shortTitles from '$lib/data/short-titles.json';

export function getNodeTitle(nodeId: string): string {
	return shortTitles[nodeId as keyof typeof shortTitles] || nodeId;
}

export function getEffectLabel(eff: { name: string; value: number | string }): string {
	const labels: Record<string, string> = {
		talent_regen_ammo: 'regen',
		talent_ranged_regen_echo_chance: 'echo chance',
		talent_defence_boost: 'defence',
		talent_all_style_accuracy: 'accuracy',
		talent_percentage_ranged_damage: 'ranged dmg',
		talent_percentage_magic_damage: 'magic dmg',
		talent_percentage_melee_damage: 'melee dmg',
		talent_prayer_pen_all: 'prayer pen',
		talent_overhealing_via_talents: 'overheal',
		talent_distance_melee_minhit: 'min hit',
		talent_melee_distance_healing_chance: 'heal chance',
		talent_thorns_damage: 'thorns',
		talent_free_random_weapon_attack_chance: 'blindbag',
		talent_regen_magic_level_boost: 'magic lvl',
		talent_airrune_regen_prayer_restore: 'prayer restore',
		talent_waterrune_regen_healing: 'healing',
		talent_firerune_regen_damage_boost: 'spell dmg',
		talent_earthrune_regen_defence_boost: 'def boost',
		talent_spec_for_free: 'free spec',
		talent_hit_restore_spec_energy: 'spec energy',
		talent_thrown_weapon_accuracy: 'thrown acc',
		talent_melee_range_multiplier: 'melee range',
		talent_crossbow_echo_reproc_chance: 'echo chance',
		talent_thrown_maxhit_echoes: 'echo max',
		talent_magic_attack_speed_traditional: 'spell speed',
		talent_magic_attack_speed_powered: 'staff speed',
		talent_air_spell_damage_active_prayers: 'air dmg',
		talent_air_spell_max_hit_prayer_bonus: 'air max',
		talent_earth_scale_defence_stat: 'earth dmg',
		talent_percentage_melee_maxhit_distance: 'max hit',
		talent_unique_blindbag_damage: 'blindbag dmg',
	};
	return labels[eff.name] || '';
}

export function getEffectDisplay(node: GraphNode, overrideEffect?: { name: string; value: number | string }): string {
	const eff = overrideEffect || node.effect;
	if (!eff || eff.value === undefined) return '';
	const v = eff.value;
	if (typeof v === 'string') return '';
	if (typeof v === 'number') {
		const isPercent = (node.label || '').includes('#%');
		return v + (isPercent ? '%' : '');
	}
	return '';
}

export function getPlainLabel(node: GraphNode): string {
	const raw = (node.label || '').split('\n')[0] || node.id;
	return raw
		.replace(/<col=\*>/g, '')
		.replace(/<\/col>/g, '')
		.trim();
}

export function getShortLabel(node: GraphNode): string {
	const title = getNodeTitle(node.id);
	const effect = getEffectDisplay(node);
	if (effect && title !== node.id) {
		const isPercent = typeof node.effect?.value === 'number' && title.includes('%');
		return `${title} (+${effect}${isPercent ? '' : ''})`;
	}
	if (effect) return `${title} (+${effect})`;
	return title;
}

export function formatDescription(node: GraphNode): string {
	let cleanDesc = (node.label || 'No description')
		.replace(/<col=\*>/g, '<span style="color:var(--color-accent-primary);font-weight:600">')
		.replace(/<\/col>/g, '</span>')
		.replace(/\n/g, '<br>');

	if (node.effect && node.effect.value !== undefined) {
		const value = node.effect.value;
		const display = typeof value === 'string' && value.startsWith('[Constant:')
			? value.replace('[Constant: ', '').replace(']', '')
			: value;
		cleanDesc = cleanDesc
			.replace(/#%/g, typeof display === 'number' ? display + '%' : String(display))
			.replace(/#(?![a-zA-Z])/g, String(display));
	}

	return cleanDesc;
}

export function generateTooltipHTML(node: GraphNode, iconHref?: string): string {
	const color = typeColors[(node.type || 'general') as keyof typeof typeColors];
	const typeName = (node.type || 'general').toUpperCase();
	const sizeName = (node.size || '').replace('node_', '').toUpperCase();
	const isRoot = node.id === 'node1';

	const iconHtml = iconHref
		? `<img src="${iconHref}" class="tooltip-icon" style="border: 2px solid ${color};background:color-mix(in srgb, ${color} 18%, transparent)">`
		: '';

	const cleanDesc = formatDescription(node);
	const title = getNodeTitle(node.id);

	// Build the right-side content (title, type, description, effects)
	let rightContent = `
		<div class="tooltip-title">${isRoot ? 'START' : title}</div>
		<div class="tooltip-type" style="background:color-mix(in srgb, ${color} 18%, transparent);color:${color};border:1px solid color-mix(in srgb, ${color} 38%, transparent)">${typeName}${sizeName ? ' &middot; ' + sizeName : ''}</div>
		<div class="tooltip-desc">${cleanDesc}</div>
	`;

	if (node.effect) {
		const display = getEffectDisplay(node);
		if (display) {
			rightContent += `
				<div class="tooltip-effect">
					<strong>Value:</strong> <span style="color:var(--color-accent-primary)">+${display}</span>
				</div>
			`;
		}
	}

	if (node.secondary_effect) {
		const secDisplay = getEffectDisplay(node, node.secondary_effect);
		if (secDisplay) {
			rightContent += `
				<div class="tooltip-effect" style="color:var(--color-info)">
					<strong>Bonus:</strong> +${secDisplay} all-style accuracy
				</div>
			`;
		}
	}

	// Layout: icon on left, all content on right
	const html = `
		<div class="tooltip-layout">
			${iconHtml}
			<div class="tooltip-content">
				${rightContent}
			</div>
		</div>
	`;

	return html;
}
